import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { OrganizationUnit, useMyUnits } from '~/data/orgunits';
import { StoreState } from '~/reducer';
import {
  orgUnitsStoreStateSelector,
  surveyStoreStateSelector,
} from '~/selectors';
import { AppDispatch } from '~/store';
import {
  commonPDFStyleOverrides,
  generateSolaforcePDFFromReact,
} from '~/utils/generatePDF';

import {
  closeAnswerAndResultsDialog,
  fetchFilteredSurveyResults,
  fetchSurveyAnswers,
  fetchSurveyResults,
} from '../slice';
import { ChildInput, SurveyResultsObject, UnitType } from '../types';

import { exportSurveyResultsToExcel } from './ExportSurveyResults';

export type UseSurveyResultsDialogOutput = {
  open: boolean;
  onDialogClose: () => void;
  surveyResults: SurveyResultsObject;
  menuList: ChildInput[];
  selectValue: number;
  selectOnChange: (value: number) => void;
  isLoading: boolean;
  failedToLoad: boolean;
  exportResults: () => void;
  exportToPdf: () => void;
};

export const useSurveyResults = (
  resultId: string,
  handleResultsClose: () => void,
  pdfContentRef?: React.RefObject<HTMLElement>,
): UseSurveyResultsDialogOutput => {
  const {
    surveyReviewers,
    surveyAudience,
    surveyQuestions,
    surveyRawResults,
    surveyResults,
  } = useSelector((state: StoreState) => {
    return surveyStoreStateSelector(state);
  });
  const { isLoading, failedToLoad } = useSelector((state: StoreState) => {
    return orgUnitsStoreStateSelector(state);
  });
  const [selectValue, setSelectValue] = useState<number>(-1);
  const { t } = useTranslation();
  const allLabel = t('label_all_units');
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const { myUnits } = useMyUnits();

  const isOrgUnitInUnitTypes = useCallback(
    (orgUnit: OrganizationUnit, units: UnitType[]) =>
      !!units
        ? units.some(
            (r: UnitType) => r.id === orgUnit.fOrgUnitId && r.type === 'UNIT',
          )
        : true,
    [],
  );

  const hasReviewAccess = useMemo((): boolean => {
    const reviewer = {
      units: surveyReviewers.filter((r) => r.type === 'UNIT'),
      employees: surveyReviewers.filter((r) => r.type === 'EMPLOYEE'),
    };

    const hasUnitBasedAccess = myUnits.some((orgUnit) =>
      isOrgUnitInUnitTypes(orgUnit, reviewer.units),
    );

    const hasNamedEmployeeAccess = reviewer.employees.some(
      (r) => r.id === loggedInEmployeeId,
    );

    return hasUnitBasedAccess || hasNamedEmployeeAccess;
  }, [surveyReviewers, isOrgUnitInUnitTypes, loggedInEmployeeId, myUnits]);

  const unitList: ChildInput[] = hasReviewAccess
    ? myUnits
        .filter((orgUnit) => isOrgUnitInUnitTypes(orgUnit, surveyAudience))
        .map((unit): ChildInput => {
          return {
            value: unit.fOrgUnitId,
            label: unit.fOrgUnitName,
          };
        })
    : [];

  const dispatch: AppDispatch = useDispatch();
  const [open, setOpen] = useState<boolean>(false);

  const onDialogClose = useCallback(() => {
    handleResultsClose();
    setOpen(false);
    (async () => {
      await dispatch(closeAnswerAndResultsDialog());
    })();
  }, [handleResultsClose, dispatch]);

  useEffect(() => {
    setOpen(Boolean(resultId));
  }, [resultId]);

  useEffect(() => {
    (async () => {
      if (selectValue === -1) {
        await dispatch(fetchSurveyResults(resultId));
      } else {
        await dispatch(
          fetchFilteredSurveyResults({ id: resultId, unitId: selectValue }),
        );
      }
    })();
  }, [dispatch, resultId, selectValue]);

  useEffect(() => {
    (async () => {
      await dispatch(fetchSurveyAnswers(resultId));
    })();
  }, [dispatch, resultId]);

  const selectOnChange = (value: number) => {
    setSelectValue(value);
  };

  const exportResults = () => {
    exportSurveyResultsToExcel(surveyQuestions, surveyRawResults);
  };

  const exportToPdf = () => {
    const content = pdfContentRef?.current;
    if (!content) {
      return;
    }

    const baseFileName = surveyResults?.name || `survey-results-${resultId}`;

    generateSolaforcePDFFromReact({
      content,
      fileName: baseFileName,
      documentDetails: {
        title: surveyResults?.name ?? t('laSurveyResults'),
        subtitle: moment().format('L'),
      },
      config: {
        styleOverrideRules: [commonPDFStyleOverrides.muiPaper],
      },
    });
  };

  return {
    surveyResults,
    open,
    onDialogClose,
    menuList: [{ label: allLabel, value: -1 }, ...unitList],
    selectValue,
    selectOnChange,
    isLoading,
    failedToLoad,
    exportResults,
    exportToPdf,
  };
};
